import React, { useEffect } from 'react';
import { SalesOrderDetailDataForm } from '../../../../app/models/SalesOrderDetail';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import { Product } from '../../../../app/models/Product';
import { Unit } from '../../../../app/models/Unit';
import { Currency } from '../../../../app/models/Currency';
interface SalesOrderDetailFormProps {
    salesOrderDetail: SalesOrderDetailDataForm;
    setSalesOrderDetail: (detail: SalesOrderDetailDataForm) => void;
    products: Product[];
    units: Unit[];
    currencies: Currency[];
    errorFields?: any;
}

const SalesOrderDetailForm = ({
    salesOrderDetail,
    setSalesOrderDetail,
    products,
    units,
    currencies,
    errorFields
}: SalesOrderDetailFormProps) => {
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields]);

    const handleChangeSelectTwo = (name: string, value: number | string | null) => {
        setSalesOrderDetail({ ...salesOrderDetail, [name]: value ?? undefined });
    };

    const handleChangeProduct = (productId: number | null) => {
        if (!productId) {
            setSalesOrderDetail({
                ...salesOrderDetail,
                product_id: undefined,
                quantity_unit_id: undefined,
                price_unit_id: undefined
            });
            return;
        }

        const selected = products.find(p => p.id === productId);
        const unitId = selected?.unit_id ?? (selected?.unit as Unit | undefined)?.id ?? undefined;

        setSalesOrderDetail({
            ...salesOrderDetail,
            product_id: productId,
            quantity_unit_id: unitId,
            price_unit_id: unitId
        });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = e.target;
        if (type === 'date') {
            setSalesOrderDetail({ 
                ...salesOrderDetail, 
                [name]: value || undefined
            });
        } else {
            setSalesOrderDetail({ 
                ...salesOrderDetail, 
                [name]: value ? parseFloat(value) : 0 
            });
        }
    };

    if (!salesOrderDetail) return null;

    return (
        <div className="container-fluid px-0">
            {/* Producto */}
            <div className="mb-3 row">
                <div className="col-12">
                    <label className="col-form-label fw-semibold">Producto</label>
                    <SelectTwo
                        name="product_id"
                        id="product_id"
                        inputValue={salesOrderDetail.product_id}
                        options={SelectTwoMapperOptions(products, 'id', ['name'])}
                        hasError={fieldHasError('product_id') !== ''}
                        onChange={(value: any) => handleChangeProduct(value?.value ?? null)}
                        placeholder="Seleccione un producto"
                        onFocus={() => onFocusRemove('product_id')}
                        isClearable={true}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('product_id')}
                    </div>
                </div>
            </div>

            {/* Cantidad y Unidad de Cantidad */}
            <div className="mb-3 row">
                <div className="col-6">
                    <label className="col-form-label fw-semibold">Cantidad <span className="text-danger">*</span></label>
                    <input
                        type="number"
                        className={`form-control ${fieldHasError('quantity')}`}
                        name="quantity"
                        value={salesOrderDetail.quantity || ''}
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        onFocus={() => onFocusRemove('quantity')}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('quantity')}
                    </div>
                </div>
                <div className="col-6">
                    <label className="col-form-label fw-semibold">Unidad</label>
                    <SelectTwo
                        name="quantity_unit_id"
                        id="quantity_unit_id"
                        inputValue={salesOrderDetail.quantity_unit_id}
                        options={SelectTwoMapperOptions(units, 'id', ['name'])}
                        hasError={fieldHasError('quantity_unit_id') !== ''}
                        onChange={(value: any) => handleChangeSelectTwo('quantity_unit_id', value?.value)}
                        placeholder=" Unidad"
                        onFocus={() => onFocusRemove('quantity_unit_id')}
                        isClearable={true}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('quantity_unit_id')}
                    </div>
                </div>
            </div>

            {/* Precio, Moneda y Unidad del Precio */}
            <div className="mb-3 row">
                <div className="col-4">
                    <label className="col-form-label fw-semibold">Precio <span className="text-danger">*</span></label>
                    <input
                        type="number"
                        className={`form-control ${fieldHasError('price')}`}
                        name="price"
                        value={salesOrderDetail.price || ''}
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        onFocus={() => onFocusRemove('price')}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('price')}
                    </div>
                </div>
                <div className="col-4">
                    <label className="col-form-label fw-semibold">Moneda</label>
                    <SelectTwo
                        name="price_currency_id"
                        id="price_currency_id"
                        inputValue={salesOrderDetail.price_currency_id}
                        options={SelectTwoMapperOptions(currencies, 'id', ['iso_code'])}
                        hasError={fieldHasError('price_currency_id') !== ''}
                        onChange={(value: any) => handleChangeSelectTwo('price_currency_id', value?.value)}
                        placeholder="Moneda"
                        onFocus={() => onFocusRemove('price_currency_id')}
                        isClearable={true}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('price_currency_id')}
                    </div>
                </div>
                <div className="col-4">
                    <label className="col-form-label fw-semibold">Unidad</label>
                    <SelectTwo
                        name="price_unit_id"
                        id="price_unit_id"
                        inputValue={salesOrderDetail.price_unit_id}
                        options={SelectTwoMapperOptions(units, 'id', ['name'])}
                        hasError={fieldHasError('price_unit_id') !== ''}
                        onChange={(value: any) => handleChangeSelectTwo('price_unit_id', value?.value)}
                        placeholder="Unidad"
                        onFocus={() => onFocusRemove('price_unit_id')}
                        isClearable={true}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('price_unit_id')}
                    </div>
                </div>
            </div>

            {/* Fecha de Pedido */}
            <div className="mb-3 row">
                <div className="col-6">
                    <label className="col-form-label fw-semibold">Fecha de Pedido</label>
                    <input
                        type="date"
                        className={`form-control ${fieldHasError('order_date')}`}
                        name="order_date"
                        value={salesOrderDetail.order_date || ''}
                        onChange={handleChange}
                        onFocus={() => onFocusRemove('order_date')}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('order_date')}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SalesOrderDetailForm;

